import { Check, ChevronDown } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import './CustomSelect.css';

export interface CustomSelectOption {
  value: string;
  label: string;
}

export interface CustomSelectProps {
  id?: string;
  value: string;
  options: CustomSelectOption[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  id,
  value,
  options,
  onChange,
  onBlur,
  disabled,
  placeholder = '— Select —',
  className,
  style,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value);
  const label = selected ? selected.label : placeholder;

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onBlur]);

  const handleSelect = (val: string) => {
    onChange(val);
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((v) => !v);
    }
    if (e.key === 'Escape') {
      setOpen(false);
      onBlur?.();
    }
    if (e.key === 'ArrowDown' && !open) {
      e.preventDefault();
      setOpen(true);
    }
  };

  return (
    <div
      ref={ref}
      id={id}
      className={[
        'custom-select',
        open ? 'custom-select--open' : '',
        disabled ? 'custom-select--disabled' : '',
        className ?? '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={style}
      role="combobox"
      aria-expanded={open}
      aria-haspopup="listbox"
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && setOpen((v) => !v)}
      onKeyDown={handleKeyDown}
      onBlur={(e) => {
        if (ref.current && !ref.current.contains(e.relatedTarget as Node)) {
          setOpen(false);
          onBlur?.();
        }
      }}
    >
      <span className="custom-select__value">{label}</span>
      <ChevronDown className="custom-select__chevron" size={14} aria-hidden="true" />

      {open && (
        <ul className="custom-select__list" role="listbox">
          {options.map((o) => (
            <li
              key={o.value}
              role="option"
              aria-selected={value === o.value}
              className={`custom-select__option${value === o.value ? ' custom-select__option--active' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(o.value);
              }}
            >
              <span>{o.label}</span>
              {value === o.value && <Check size={12} className="custom-select__check" />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
